"use client"

import {
  forwardRef,
  type CSSProperties,
  type HTMLAttributes,
  useLayoutEffect,
  useRef,
} from "react"

import { cn } from "@/lib/utils"

type PanelRect = {
  left: number
  top: number
  width: number
  height: number
}

type ZoomOriginPanelProps = HTMLAttributes<HTMLDivElement> & {
  visible: boolean
  originRect: DOMRect | null
  targetRect: PanelRect
  animationMs?: number
  onHeightChange?: (height: number) => void
}

export const ZoomOriginPanel = forwardRef<HTMLDivElement, ZoomOriginPanelProps>(
  function ZoomOriginPanel(
    {
      visible,
      originRect,
      targetRect,
      animationMs = 280,
      onHeightChange,
      className,
      style,
      ...props
    },
    forwardedRef
  ) {
    const localRef = useRef<HTMLDivElement | null>(null)

    useLayoutEffect(() => {
      const el = localRef.current
      if (!el || !onHeightChange) return

      const measure = () => {
        onHeightChange(el.offsetHeight)
      }

      const observer = new ResizeObserver(measure)
      observer.observe(el)
      measure()

      return () => observer.disconnect()
    }, [onHeightChange])

    const animationStyle: CSSProperties = (() => {
      const baseStyle: CSSProperties = {
        pointerEvents: visible ? "auto" : "none",
      }

      if (!originRect) {
        return visible ? baseStyle : { ...baseStyle, opacity: 0 }
      }

      const targetCenterX = targetRect.left + targetRect.width / 2
      const targetCenterY = targetRect.top + targetRect.height / 2
      const originCenterX = originRect.left + originRect.width / 2
      const originCenterY = originRect.top + originRect.height / 2
      const translateX = originCenterX - targetCenterX
      const translateY = originCenterY - targetCenterY
      const scaleX = Math.min(
        Math.max(originRect.width / targetRect.width, 0.08),
        1
      )
      const scaleY = Math.min(
        Math.max(originRect.height / targetRect.height, 0.08),
        1
      )

      return {
        ...baseStyle,
        opacity: visible ? 1 : 0.2,
        transform: visible
          ? "translate3d(0, 0, 0) scale(1)"
          : `translate3d(${translateX}px, ${translateY}px, 0) scale(${scaleX}, ${scaleY})`,
        transformOrigin: "center center",
        transitionProperty: "transform, opacity",
        transitionDuration: `${animationMs}ms`,
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
        willChange: "transform, opacity",
      }
    })()

    return (
      <div
        {...props}
        ref={(node) => {
          localRef.current = node

          if (typeof forwardedRef === "function") {
            forwardedRef(node)
            return
          }

          if (forwardedRef) {
            forwardedRef.current = node
          }
        }}
        className={cn(className)}
        style={{ ...style, ...animationStyle }}
      />
    )
  }
)
